import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, LayoutDashboardIcon, PackageIcon, TargetIcon, MessageSquareIcon, SparklesIcon, BarChart3Icon, SettingsIcon, ShieldCheckIcon, SendIcon, CheckIcon } from 'lucide-react';
export type AccessLevel = 'none' | 'view' | 'edit' | 'full';
export interface ModuleDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  caps: Record<AccessLevel, string>;
}
export const MODULES: ModuleDef[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: <LayoutDashboardIcon className="w-4 h-4" data-id="element-2348" data-mp="3410453z3off6" />,
  caps: {
    none: 'Hidden',
    view: 'See business overview',
    edit: 'See business overview',
    full: 'See business overview'
  }
}, {
  id: 'catalogue',
  label: 'Catalogue',
  icon: <PackageIcon className="w-4 h-4" data-id="element-2349" data-mp="1nmqf07n1amna" />,
  caps: {
    none: 'No access',
    view: 'View items only',
    edit: 'Add, edit & publish items',
    full: 'Add, edit, publish & delete items'
  }
}, {
  id: 'leads',
  label: 'Leads',
  icon: <TargetIcon className="w-4 h-4" data-id="element-2350" data-mp="1jke7qndicadh" />,
  caps: {
    none: 'No access',
    view: 'View leads & AI insights',
    edit: 'Update status, owner & notes',
    full: 'Manage, reassign & export leads'
  }
}, {
  id: 'conversations',
  label: 'Conversations',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-2351" data-mp="1elt7iny99qp1" />,
  caps: {
    none: 'No access',
    view: 'Read conversations',
    edit: 'Read & reply to customers',
    full: 'Read, reply & manage chats'
  }
}, {
  id: 'ai',
  label: 'AI Assistant',
  icon: <SparklesIcon className="w-4 h-4" data-id="element-2352" data-mp="qybiv1aaf8qa" />,
  caps: {
    none: 'No access',
    view: 'View AI knowledge',
    edit: 'Teach & edit AI rules',
    full: 'Configure all AI behavior'
  }
}, {
  id: 'analytics',
  label: 'Analytics',
  icon: <BarChart3Icon className="w-4 h-4" data-id="element-2353" data-mp="hz80y0muwzdd" />,
  caps: {
    none: 'No access',
    view: 'View reports',
    edit: 'View & export reports',
    full: 'View, export & act on insights'
  }
}, {
  id: 'settings',
  label: 'Settings',
  icon: <SettingsIcon className="w-4 h-4" data-id="element-2354" data-mp="2jtmbmriz8yel" />,
  caps: {
    none: 'No access',
    view: 'View settings',
    edit: 'Edit business profile',
    full: 'Manage team, billing & profile'
  }
}];
export type AccessMap = Record<string, AccessLevel>;
export interface TeamMember {
  name: string;
  email: string;
  role: string;
  access: AccessMap;
  canViewContact: boolean;
}
const LEVELS: AccessLevel[] = ['none', 'view', 'edit', 'full'];
const LEVEL_LABEL: Record<AccessLevel, string> = {
  none: 'None',
  view: 'View',
  edit: 'Edit',
  full: 'Full'
};
export const ROLE_PRESETS: {
  id: string;
  label: string;
  desc: string;
  access: AccessMap;
}[] = [{
  id: 'Admin',
  label: 'Admin',
  desc: 'Full access to everything',
  access: {
    dashboard: 'full',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'full',
    analytics: 'full',
    settings: 'full'
  }
}, {
  id: 'Manager',
  label: 'Manager',
  desc: 'Runs operations, no billing',
  access: {
    dashboard: 'view',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'edit',
    analytics: 'edit',
    settings: 'view'
  }
}, {
  id: 'Sales',
  label: 'Sales',
  desc: 'Works leads & conversations',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'edit',
    conversations: 'edit',
    ai: 'view',
    analytics: 'view',
    settings: 'none'
  }
}, {
  id: 'Viewer',
  label: 'Viewer',
  desc: 'Read-only access',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'view',
    conversations: 'view',
    ai: 'view',
    analytics: 'view',
    settings: 'none'
  }
}];
interface InviteMemberModalProps {
  member?: TeamMember | null;
  onClose: () => void;
  onSave: (member: TeamMember) => void;
}
export function InviteMemberModal({
  member,
  onClose,
  onSave
}: InviteMemberModalProps) {
  const isEdit = !!member;
  const [name, setName] = useState(member?.name ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [role, setRole] = useState(member?.role ?? 'Sales');
  const [access, setAccess] = useState<AccessMap>(member?.access ?? ROLE_PRESETS.find(r => r.id === 'Sales')!.access);
  const [canViewContact, setCanViewContact] = useState(member?.canViewContact ?? false);
  const applyPreset = (presetId: string) => {
    const preset = ROLE_PRESETS.find(r => r.id === presetId);
    if (!preset) return;
    setRole(presetId);
    setAccess(preset.access);
  };
  const setModuleLevel = (moduleId: string, level: AccessLevel) => {
    setAccess(prev => ({
      ...prev,
      [moduleId]: level
    }));
    setRole('Custom');
  };
  const handleSave = () => {
    onSave({
      name: name || 'New Member',
      email,
      role,
      access,
      canViewContact
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2355" data-mp="360gegf1ue1s1">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-2356" data-mp="3athvotpiudes" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2357" data-mp="2x2wb90kf5ves">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-2358" data-mp="3fs2tynl7qn0l">
          <div data-id="element-2359" data-mp="3kq4fdf642yf8">
            <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-2360" data-mp="3kpukpea4g337">
              {isEdit ? `Edit access — ${member?.name}` : 'Invite team member'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-2361" data-mp="29ca1n4uro32o">
              Choose a role, then fine-tune what they can do in each module.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2362" data-mp="exmr4l178b9e">
            <XIcon className="w-5 h-5" data-id="element-2363" data-mp="54e915lg7vfc" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2364" data-mp="13c9ennmdhcu">
          {/* Identity */}
          {!isEdit && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2365" data-mp="3sbzsewye0sh8">
              <div data-id="element-2366" data-mp="3nb2bsot106hu">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2367" data-mp="3n9qzu1pffsfn">
                  Full name
                </label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Layla Ahmed" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2368" data-mp="3ie26ig4037rs" />
              </div>
              <div data-id="element-2369" data-mp="2ufuk8n836mwh">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2370" data-mp="2uge100m0358x">
                  Email
                </label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="name@email.com" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2371" data-mp="2zdnohaptx6u4" />
              </div>
            </div>}

          {/* Role presets */}
          <div data-id="element-2372" data-mp="2kigdjfbytchd">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2373" data-mp="2khyanzuss00h">
              Role
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-2374" data-mp="2pj445zwa7rb2">
              {ROLE_PRESETS.map(p => <button key={p.id} onClick={() => applyPreset(p.id)} className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${role === p.id ? 'border-primary-indigo bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2375" data-mp="3vzzdmyu90zbp">
                  <div className={`text-sm font-bold ${role === p.id ? 'text-primary-indigo' : 'text-dark-navy'}`} data-id="element-2376" data-mp="37ji14bl0t2z5">
                    {p.label}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-tight" data-id="element-2377" data-mp="3vax7qgjty6g7">
                    {p.desc}
                  </div>
                </button>)}
            </div>
            {role === 'Custom' && <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-ai-purple bg-purple-50 px-2.5 py-1 rounded-full" data-id="element-2378" data-mp="86hbhyvym17e">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-2379" data-mp="45drr1asjl0w" /> Custom permissions
              </div>}
          </div>

          {/* Permission matrix */}
          <div data-id="element-2380" data-mp="25wqzrz822916">
            <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-2381" data-mp="25xaez86bmjbi">
              Module access
            </label>
            <div className="border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden" data-id="element-2382" data-mp="1vu54uk5ukk1g">
              {MODULES.map(mod => {
              const level = access[mod.id] ?? 'none';
              return <div key={mod.id} className="p-4" data-id="element-2383" data-mp="1r12awax5r8kq">
                    <div className="flex items-center justify-between gap-3 mb-2" data-id="element-2384" data-mp="1qzr2fxsrpcna">
                      <div className="flex items-center gap-2.5 min-w-0" data-id="element-2385" data-mp="1c39wxn79es8n">
                        <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-2386" data-mp="1h3edtec2xoze">
                          {mod.icon}
                        </span>
                        <div className="min-w-0" data-id="element-2387" data-mp="181ocngsebbof">
                          <div className="text-sm font-medium text-dark-navy" data-id="element-2388" data-mp="184kaz685o2de">
                            {mod.label}
                          </div>
                          <div className="text-xs text-slate-500 truncate" data-id="element-2389" data-mp="32gqtyr7ajng9">
                            {mod.caps[level]}
                          </div>
                        </div>
                      </div>
                      <div className="flex bg-slate-100 rounded-lg p-0.5 flex-shrink-0" data-id="element-2390" data-mp="3of2x7spoqap0">
                        {LEVELS.map(lv => <button key={lv} onClick={() => setModuleLevel(mod.id, lv)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${level === lv ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2391" data-mp="3aktkf3qvjcbv">
                            {LEVEL_LABEL[lv]}
                          </button>)}
                      </div>
                    </div>

                    {/* Leads privacy capability */}
                    {mod.id === 'leads' && level !== 'none' && <button onClick={() => setCanViewContact(v => !v)} className="mt-2 ml-10 flex items-center gap-2 text-xs font-medium text-slate-600" data-id="element-2392" data-mp="2myd7zovnrmwf">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${canViewContact ? 'bg-primary-indigo border-primary-indigo' : 'border-slate-300'}`} data-id="element-2393" data-mp="2aegabhqb5lgu">
                          {canViewContact && <CheckIcon className="w-3 h-3 text-white" data-id="element-2394" data-mp="1vqldpq96iwyh" />}
                        </span>
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2395" data-mp="1qonswrl4ru3f" />
                        Can view customer contact details
                      </button>}
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-2396" data-mp="1808ydubltxxv">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-2397" data-mp="382i9jlaa06ie">
            Cancel
          </button>
          <button onClick={handleSave} className="px-5 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-2398" data-mp="3hsa78odo9fr3">
            {isEdit ? <>
                <CheckIcon className="w-4 h-4" data-id="element-2399" data-mp="2p4xwdbgviwdm" /> Save Access
              </> : <>
                <SendIcon className="w-4 h-4" data-id="element-2400" data-mp="2u3949gnlywt7" /> Send Invite
              </>}
          </button>
        </div>
      </motion.div>
    </div>;
}
export function accessSummary(access: AccessMap): string {
  const active = MODULES.filter(m => (access[m.id] ?? 'none') !== 'none').length;
  return `${active} module${active === 1 ? '' : 's'}`;
}